import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";

/* Thin filling bar (0–12°C). 2–8°C is the safe band; live pulse shows a streaming reading. */
const SCALE_MIN = 0;
const SCALE_MAX = 12;
const SAFE_MIN = 2;
const SAFE_MAX = 8;
const STALE_AFTER_SEC = 120;

function pct(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, ((n - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));
}

function statusFor(temp) {
  if (temp == null || Number.isNaN(Number(temp))) {
    return { label: "Waiting", short: "—", tone: "muted", fill: "#d1d5db" };
  }
  const t = Number(temp);
  if (t < SAFE_MIN) return { label: "Too cold", short: "Too cold", tone: "bad", fill: "#0ea5e9" };
  if (t > SAFE_MAX) return { label: "Too warm", short: "Too warm", tone: "bad", fill: "#ef4444" };
  return { label: "Safe", short: "Safe", tone: "ok", fill: "#22c55e" };
}

function secondsAgo(iso) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.round((Date.now() - ms) / 1000));
}

function formatAgo(sec) {
  if (sec == null) return "";
  if (sec < 4) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

export default function IceboxTempBar({
  temp,
  lastUpdated,
  compact = false,
  live,
  explain = false,
  className = "",
}) {
  const has = temp != null && !Number.isNaN(Number(temp));
  const t = has ? Number(temp) : null;
  const st = statusFor(t);
  const targetPct = has ? pct(t) : 0;
  const [fillPct, setFillPct] = useState(0);
  const [nowTick, setNowTick] = useState(0);
  const [pop, setPop] = useState(false);
  const prevTemp = useRef(temp);

  useEffect(() => {
    const id = requestAnimationFrame(() => setFillPct(targetPct));
    return () => cancelAnimationFrame(id);
  }, [targetPct]);

  useEffect(() => {
    if (prevTemp.current !== temp && has) {
      setPop(true);
      const id = setTimeout(() => setPop(false), 600);
      prevTemp.current = temp;
      return () => clearTimeout(id);
    }
    prevTemp.current = temp;
    return undefined;
  }, [temp, has]);

  const showLive = live !== false;
  useEffect(() => {
    if (!showLive) return undefined;
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [showLive]);

  const agoSec = secondsAgo(lastUpdated);
  void nowTick;
  const stale = showLive && agoSec != null && agoSec > STALE_AFTER_SEC;
  const streaming = showLive && has && !stale;
  const toneClass = st.tone === "ok" ? "text-emerald-600" : st.tone === "bad" ? "text-red-500" : "text-[#9ca3af]";
  const barH = compact ? 4 : 5;
  const safeLeft = pct(SAFE_MIN);
  const safeWidth = pct(SAFE_MAX) - pct(SAFE_MIN);

  const liveBadge = streaming ? (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
      <span className={`inline-block rounded-full bg-emerald-500 ${compact ? "h-1.5 w-1.5" : "h-2 w-2"} icebox-live-dot`} />
      Live
    </span>
  ) : stale ? (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700">
      <span className="inline-block h-2 w-2 rounded-full bg-amber-500 icebox-live-dot" />
      Reconnecting
    </span>
  ) : showLive ? (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#9ca3af]">
      <span className="inline-block h-2 w-2 rounded-full bg-gray-300" />
      Waiting
    </span>
  ) : null;

  const statusBadge = has && (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${st.tone === "ok" ? "text-emerald-700" : st.tone === "bad" ? "text-red-600" : "text-[#6b7280]"}`}>
      {st.tone === "ok" && <CheckCircle2 className="w-3.5 h-3.5" />}
      {st.label}
    </span>
  );

  return (
    <div
      className={`${compact ? "w-full min-w-[120px]" : "rounded-2xl border border-black/[0.06] bg-[#fbfcfd] p-4"} ${className}`}
      data-testid="icebox-temp-bar"
    >
      {!compact && (
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[13px] font-semibold text-[#374151]">Cold-Chain Monitor</p>
          <div className="flex items-center gap-3">
            {liveBadge}
            {statusBadge}
          </div>
        </div>
      )}

      <div className={`flex items-end justify-between gap-2 ${compact ? "mb-1" : "mb-3"}`}>
        <div className="flex items-baseline gap-2 min-w-0">
          <p
            className={`font-semibold tabular-nums leading-none ${compact ? "text-base" : "text-[28px]"} ${toneClass} ${pop ? "icebox-temp-pop" : ""}`}
          >
            {has ? `${t.toFixed(1)}°C` : "—"}
          </p>
          {!compact && (
            <span className="text-xs text-[#9ca3af]">Target: 2–8°C</span>
          )}
        </div>
        {compact ? (
          <div className="flex items-center gap-2 shrink-0">
            {liveBadge}
            {statusBadge}
          </div>
        ) : (
          showLive && (
            <span className="text-[10px] text-[#9ca3af] shrink-0 tabular-nums">
              {streaming ? `Streaming · ${formatAgo(agoSec) || "live"}` : stale ? formatAgo(agoSec) : "No packet yet"}
            </span>
          )
        )}
      </div>

      <div
        className="relative w-full"
        style={{ height: compact ? 10 : 14 }}
        role="meter"
        aria-valuemin={SCALE_MIN}
        aria-valuemax={SCALE_MAX}
        aria-valuenow={has ? Number(t.toFixed(1)) : undefined}
        aria-valuetext={has ? `${t.toFixed(1)} degrees Celsius, ${st.label}` : "No temperature reading"}
        aria-label="Live icebox temperature, safe between 2 and 8 degrees Celsius"
      >
        <div
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 rounded-full bg-[#eef2f0]"
          style={{ height: barH }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded-full bg-emerald-500/15"
          style={{ left: `${safeLeft}%`, width: `${safeWidth}%`, height: barH }}
          title="Safe zone 2–8°C"
        />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 overflow-hidden rounded-full"
          style={{
            width: `${fillPct}%`,
            height: barH,
            background: st.fill,
            transition: "width 0.8s ease-out",
          }}
        >
          {streaming && (
            <div
              className="absolute inset-y-0 w-1/2 icebox-fill-shimmer"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
              }}
            />
          )}
        </div>
        {has && (
          <div
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full ${streaming ? (st.tone === "bad" ? "icebox-live-dot-warn" : "icebox-cap-glow") : ""}`}
            style={{
              left: `${fillPct}%`,
              width: compact ? 8 : 10,
              height: compact ? 8 : 10,
              background: st.fill,
              boxShadow: `0 0 0 3px #fff, 0 0 10px ${st.fill}`,
              transition: "left 0.8s ease-out",
            }}
            aria-hidden
          />
        )}
      </div>

      {!compact && (
        <div className="relative mt-1.5 h-4 text-[10px] text-[#9ca3af] tabular-nums">
          <span className="absolute left-0">0°C</span>
          <span
            className="absolute -translate-x-1/2 text-emerald-600/80 font-medium"
            style={{ left: `${safeLeft + safeWidth / 2}%` }}
          >
            Safe Zone
          </span>
          <span className="absolute right-0">12°C</span>
        </div>
      )}
      {explain && (
        <p className={`text-[#4b5563] leading-snug ${compact ? "text-[11px] mt-1.5" : "text-xs mt-3"}`}>
          {live === false
            ? "These doses travelled in this icebox. Vaccines stay effective only at 2–8°C — heat or freezing can weaken them."
            : stale
              ? "Reconnecting to the icebox. The last reading is still on screen. A short pause in signal is not a cold-chain failure by itself — we keep watching temperature."
              : "Your vaccines are in this icebox until they are given. They stay effective only at 2–8°C. Heat or freezing can weaken a dose, so we show this temperature live."}
        </p>
      )}
    </div>
  );
}
